import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { toast } from "sonner";
import { formatEther } from "viem";
import { usePublicClient } from "wagmi";
import { GATEWAY_ABI } from "@/contract/abi/gateway";
import ContractAddress from "@/contract/addresses";
import { useEffectiveAddress } from "@/hooks/auth/useEffectiveAddress";
import { useGuardedWriteContract } from "@/hooks/auth/useGuardedWrite";
import { useContractErrorHandler } from "@/hooks/useContractErrorHandler";
import { useFetchMe } from "./useFetchMe";

export const useCalculateReward = () => {
  const t = useTranslations("stake");
  const { handleContractError } = useContractErrorHandler();
  const address = useEffectiveAddress();
  const { writeContractAsync } = useGuardedWriteContract();
  const publicClient = usePublicClient();
  const queryClient = useQueryClient();

  const { data: user } = useFetchMe();

  async function mutationFn() {
    if (!user || !user.wallet_address || !address || !publicClient) {
      throw new Error("Wait for orders to load");
    }

    const currentUser = await publicClient.readContract({
      abi: GATEWAY_ABI,
      address: ContractAddress.GATEWAY,
      functionName: "getUser",
      args: [address as `0x${string}`],
    });
    const list = user.pending_orders_list
      .map((id) => BigInt(String(id)))
      .filter((id) => id > currentUser.last_order.order_id)
      .sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));
    const firstOrder = list[0];
    if (firstOrder === undefined) {
      throw new Error(
        "No unprocessed orders available to initialize calculation",
      );
    }

    const trx = await writeContractAsync({
      abi: GATEWAY_ABI,
      address: ContractAddress.GATEWAY,
      functionName: "calculate",
      args: [[firstOrder]],
    });

    if (trx && publicClient) {
      const initialization = await publicClient.waitForTransactionReceipt({
        hash: trx,
      });
      if (initialization.status !== "success") {
        throw new Error("Transaction failed!");
      }
      // Even if the next signature is cancelled, the confirmed initialization
      // has advanced the cursor and must be reflected in the remaining count.
      await queryClient.invalidateQueries({ queryKey: ["order-behind"] });
      const tx = await writeContractAsync({
        abi: GATEWAY_ABI,
        functionName: "calculateWeek",
        address: ContractAddress.GATEWAY,
      });
      if (tx) {
        const receipt = await publicClient.waitForTransactionReceipt({
          hash: tx,
        });
        if (receipt.status !== "success") {
          throw new Error("Transaction failed!");
        }
      } else {
        throw new Error("Transaction failed!");
      }

      const new_user = await publicClient.readContract({
        abi: GATEWAY_ABI,
        address: ContractAddress.GATEWAY,
        functionName: "getUser",
        args: [address as `0x${string}`],
      });

      return {
        left: +formatEther(new_user.bv.left),
        right: +formatEther(new_user.bv.right),
      };
    } else {
      throw new Error("Transaction failed!");
    }
  }

  return useMutation({
    mutationFn,
    mutationKey: ["calculate_reward", address],
    onError: (e) => handleContractError(e),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["order-behind"],
      });
      toast.success(t("toasts.calculateSuccess"));
    },
  });
};
